import React, {Component} from 'react';
import {Add, Star, StarBorder} from '@mui/icons-material';
import {Button, Chip, CircularProgress, IconButton, Tooltip} from '@mui/material';
import {connect} from 'react-redux';

import Api from '../../api';
import {isAdminPanelRole} from '../../auth/roles';
import {GET_SMS_CONFIG_URL} from '../../lib/constant';
import {isImpersonating} from '../../lib/impersonation';
import {getFromLocalStorage} from '../../lib/utils';
import {addMySenderId, setMyDefaultSenderId} from '../../actions/template';
import AddSenderId from '../AdminPanel/addSenderId';

// Headers tab — listing + set-default for everyone, plus Add. Edit/delete
// were dropped when this moved out of the Manage Headers dialog: headers
// are DLT-registered, so day-2 mutations stay in the Admin Panel (SMS
// Config tab). Add hits the customer self-service endpoint
// (POST /me/sms_config/sender_ids, gateway requires org_admin/tenant_owner),
// so it shows for customer org admins and platform tier alike — and during
// impersonation, since that session mirrors the customer org-admin view.
// Set-default is customer-scoped (me:sms_config:default:write) so the star
// shows for normal customers.
class HeadersTab extends Component {
  role = (getFromLocalStorage() || {}).role;
  canAdd = isAdminPanelRole(this.role) || this.role === 'org_admin' || isImpersonating();
  // TMD is a routing internal: hidden from customers, and an impersonation
  // session IS the customer view.
  canSeeTmd = isAdminPanelRole(this.role) && !isImpersonating();

  state = {
    loading: true,
    list: [],
    showAdd: false,
    busyId: null // row id with an in-flight set-default write
  };

  componentDidMount() {
    this.fetchList();
  }

  fetchList = async () => {
    this.setState({loading: true});
    try {
      const {data} = await Api.get(GET_SMS_CONFIG_URL);
      const list = data?.data?.senderIds || data?.data?.sender_ids || [];
      this.setState({list, loading: false});
    } catch (err) {
      this.setState({loading: false});
      alert(err?.response?.data?.message || err?.message || 'Failed to load headers');
    }
  };

  openAdd = () => this.setState({showAdd: true});
  closeAdd = () => this.setState({showAdd: false});
  handleAdd = (params) => {
    Promise.resolve(this.props.addMySenderId(params))
      .then((data) => {
        this.setState({showAdd: false});
        if (data && data.success === false) {
          alert(data.message || 'Failed to add header');
        }
        this.fetchList();
      })
      .catch((err) => {
        alert(err?.response?.data?.message || err?.message || 'Failed to add header');
      });
  };

  setDefault = (id) => {
    this.setState({busyId: id});
    Promise.resolve(this.props.setMyDefaultSenderId(id))
      .then(() => {
        this.setState({busyId: null});
        this.fetchList();
      })
      .catch((err) => {
        this.setState({busyId: null});
        alert(err?.response?.data?.message || err?.message || 'Failed to set default');
      });
  };

  renderRow = (row) => {
    const {busyId} = this.state;
    const isBusy = busyId === row.id;
    const isAlpha = row.headerType === 'HEADER_TYPE_ALPHANUMERIC' || row.headerType === 'alphanumeric';
    const headerTypeLabel = isAlpha ? 'alphanumeric' : (row.headerType === 'HEADER_TYPE_NUMERIC' ? 'numeric' : (row.headerType || '—'));
    const isActive = !row.status || row.status === 'SMS_CONFIG_STATUS_ACTIVE';
    return (
      <tr key={row.id} style={{borderBottom: '1px solid #eee'}}>
        <td style={{padding: '8px 12px', fontWeight: 600}}>{row.senderId}</td>
        <td style={{padding: '8px 12px'}}>
          <Chip size="small" label={headerTypeLabel} />
        </td>
        <td style={{padding: '8px 12px'}}>
          <Chip size="small" label={isActive ? 'active' : 'inactive'} color={isActive ? 'success' : 'default'} />
        </td>
        {this.canSeeTmd && (
          <td style={{padding: '8px 12px', fontSize: 12, color: '#666'}}>
            {(() => {
              const mode = row.dltTmdMode || row.dlt_tmd_mode || '';
              const tmd = row.dltTmdId || row.dlt_tmd_id || '';
              if (mode === 'inherit') return 'inherit (route)';
              if (mode === 'explicit' || (!mode && tmd)) return tmd || '—';
              return 'direct';
            })()}
          </td>
        )}
        <td style={{padding: '8px 12px'}}>
          {row.isDefault ? (
            <Chip size="small" color="primary" icon={<Star fontSize="small" />} label="default" />
          ) : (
            <Tooltip title="Set as default">
              <span>
                <IconButton size="small" disabled={isBusy} onClick={() => this.setDefault(row.id)}>
                  {isBusy ? <CircularProgress size={16} /> : <StarBorder fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </td>
      </tr>
    );
  };

  render() {
    const {loading, list, showAdd} = this.state;
    return (
      <div>
        <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
          <p style={{fontSize: '1rem'}}>{list.length > 0 ? list.length : ''} Headers</p>
          {this.canAdd && (
            <Button
              onClick={this.openAdd}
              startIcon={<Add />}
              variant='contained'
              style={{marginLeft: 'auto'}}
            >
              Add Header
            </Button>
          )}
        </div>
        {loading ? (
          <div style={{textAlign: 'center', padding: 32}}>
            <CircularProgress size={32} />
          </div>
        ) : list.length === 0 ? (
          <p style={{padding: 50, fontSize: 16, color: '#333'}}>
            {this.canAdd
              ? <>No headers yet. Click <b>Add Header</b> to register one.</>
              : 'No headers yet. Please contact your org admin to register one.'}
          </p>
        ) : (
          <div style={{paddingTop: 16, overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{background: '#fafafa', textAlign: 'left'}}>
                  <th style={{padding: '8px 12px'}}>Header</th>
                  <th style={{padding: '8px 12px'}}>Type</th>
                  <th style={{padding: '8px 12px'}}>Status</th>
                  {this.canSeeTmd && <th style={{padding: '8px 12px'}}>Downstream TMD</th>}
                  <th style={{padding: '8px 12px'}}>Default</th>
                </tr>
              </thead>
              <tbody>{list.map(this.renderRow)}</tbody>
            </table>
          </div>
        )}

        {showAdd && (
          <AddSenderId
            addSenderId={this.handleAdd}
            closeAddSenderId={this.closeAdd}
          />
        )}
      </div>
    );
  }
}

export default connect(null, {addMySenderId, setMyDefaultSenderId})(HeadersTab);
